import React, { useState } from 'react';

const BlockchainErrorOverlay = ({ error, onDismiss }) => {
  const [showFullError, setShowFullError] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  if (!error) return null;

  // Extract meaningful error message
  const getErrorMessage = (error) => {
    if (typeof error === 'string') return error;

    // Handle ethers/Web3 errors
    if (error.reason) return error.reason;
    if (error.message) {
      // Clean up common error messages
      let message = error.message;

      // Extract revert reasons from error messages
      if (message.includes('execution reverted:')) {
        const revertMatch = message.match(/execution reverted: (.+?)(?:\s*\(|$)/);
        if (revertMatch) return revertMatch[1];
      }

      // Handle user rejection
      if (message.includes('user rejected') || message.includes('User denied')) {
        return 'Transaction was rejected by user';
      }

      // Handle insufficient funds
      if (message.includes('insufficient funds')) {
        return 'Insufficient funds to complete transaction';
      }

      // Handle network issues
      if (message.includes('network') || message.includes('connection')) {
        return 'Network connection issue. Please check your connection and try again.';
      }

      return message;
    }

    return error.toString();
  };

  // Get full error details for debugging
  const getFullErrorDetails = (error) => {
    if (typeof error === 'string') return error;

    // Create comprehensive error information
    const details = {
      message: error.message || 'Unknown error',
      name: error.name || 'Error',
      code: error.code || null,
      reason: error.reason || null,
      method: error.method || null,
      transaction: error.transaction || null,
      receipt: error.receipt || null,
      stack: error.stack || null
    };

    // Filter out null values and format
    const filteredDetails = Object.entries(details)
      .filter(([key, value]) => value !== null && value !== undefined)
      .map(([key, value]) => {
        if (typeof value === 'object') {
          return `${key}: ${JSON.stringify(value, null, 2)}`;
        }
        return `${key}: ${value}`;
      })
      .join('\n\n');

    return filteredDetails || error.toString();
  };

  const errorMessage = getErrorMessage(error);
  const fullErrorDetails = getFullErrorDetails(error);

  // Copy full error details to clipboard
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(fullErrorDetails);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = fullErrorDetails;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-red-50 border-2 border-red-500 rounded-lg p-6 max-w-2xl mx-4 shadow-2xl max-h-[80vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="ml-3 text-lg font-semibold text-red-800">
              Blockchain Error
            </h3>
          </div>

          <button
            onClick={onDismiss}
            className="text-red-400 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 rounded-full p-1"
            aria-label="Close error message"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Main Error Message */}
        <div className="text-red-700 text-sm leading-relaxed mb-4">
          {errorMessage}
        </div>

        {/* Full Error Details - Collapsible */}
        <div className="mb-4">
          <button
            onClick={() => setShowFullError(!showFullError)}
            className="flex items-center space-x-2 text-red-600 hover:text-red-700 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 rounded-sm px-1 py-1"
          >
            <svg
              className={`h-4 w-4 transform transition-transform duration-200 ${showFullError ? 'rotate-90' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
            <span>{showFullError ? 'Hide' : 'Show'} full error details</span>
          </button>

          {showFullError && (
            <div className="mt-3 bg-red-100 border border-red-300 rounded p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-red-800 font-medium text-xs">Full Error Information:</span>
                <button
                  onClick={copyToClipboard}
                  className={`flex items-center space-x-1 px-2 py-1 text-xs rounded transition-colors ${
                    copySuccess
                      ? 'bg-green-200 text-green-800'
                      : 'bg-red-200 text-red-700 hover:bg-red-300'
                  }`}
                >
                  {copySuccess ? (
                    <>
                      <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Copied!</span>
                    </>
                  ) : (
                    <>
                      <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      <span>Copy</span>
                    </>
                  )}
                </button>
              </div>
              <pre className="text-xs text-red-700 whitespace-pre-wrap overflow-x-auto bg-white p-2 rounded border max-h-60 overflow-y-auto">
                {fullErrorDetails}
              </pre>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3">
          <button
            onClick={onDismiss}
            className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
};

export default BlockchainErrorOverlay;